import { z } from "zod";

export const INCORPORATION_REQUEST_STATUS = [
  "draft",
  "submitted",
  "approved",
  "rejected",
] as const;
export type IncorporationRequestStatus =
  (typeof INCORPORATION_REQUEST_STATUS)[number];

export const OBJETO_SOCIAL = ["Estandar", "Otro"] as const;
export type ObjetoSocialOption = (typeof OBJETO_SOCIAL)[number];

export const CAPITAL_SOCIAL_MONTO = ["estandar", "otro"] as const;
export type CapitalSocialMontoOption = (typeof CAPITAL_SOCIAL_MONTO)[number];

export const SI_NO_OPTIONS = ["si", "no"] as const;
export type SiNoOption = (typeof SI_NO_OPTIONS)[number];

export const USER_TYPE = ["Miembro", "Intermediario"] as const;
export type UserTypeOption = (typeof USER_TYPE)[number];

export const GESTION_DIRECTORES = ["abogado", "cliente"] as const;
export type GestionDirectoresOption = (typeof GESTION_DIRECTORES)[number];

export const CARGO_DIRECTOR = ["presidente", "secretario", "tesorero", "no_tendra_cargo", ""] as const;
export type CargoDirectorOption = (typeof CARGO_DIRECTOR)[number];

export const FUENTE_INGRESOS = [
  "ahorros",
  "comercializacion de bienes y servicios",
  "dividentos",
  "aporte de accionistas",
  "otros",
] as const;
export type FuenteIngresosOption = (typeof FUENTE_INGRESOS)[number];

export const CONTACT_TYPE = ["persona natural", "persona juridica"] as const;
export type ContactTypeOption = (typeof CONTACT_TYPE)[number];

export const CONTACT_ROL = ["director", "accionista"] as const;
export type ContactRolOption = (typeof CONTACT_ROL)[number];

export const DOMICILIO_PROPORCIONADO = ["cliente", "abogado"] as const;
export type DomicilioProporcionadoOption =
  (typeof DOMICILIO_PROPORCIONADO)[number];

export const CORRESPONDENCIA_TIPO = ["digital", "fisico"] as const;
export type CorrespondenciaTipoOption =
  (typeof CORRESPONDENCIA_TIPO)[number];

export const IncorporationRequestSchema = z.object({
  /** 
   * al inicial no va a haber id hasta q se haga un post elegiendo el nombre y back regrese el 
   * objeto incorporation request guardado en la db con id
   */
  id: z.number().optional(),
  user_id: z.number().optional(),
  nombre: z.string().optional(),
  status: z.enum(INCORPORATION_REQUEST_STATUS),

  objeto_social: z.enum(OBJETO_SOCIAL),
  objeto_social_otro: z.string().nullable().optional(),
  actividad_principal: z.string(),
  opera_panama: z.enum(SI_NO_OPTIONS),

  capital_social_monto: z.enum(CAPITAL_SOCIAL_MONTO),
  capital_social_monto_otro: z.string().nullable().optional(),

  user_type: z.enum(USER_TYPE),
  intermediario_nombre: z.string().nullable().optional(),
  intermediario_email: z.string().email().nullable().optional(),
  intermediario_telefono: z.string().nullable().optional(),

  gestion_directores: z.enum(GESTION_DIRECTORES),

  fuente_ingresos: z.array(z.enum(FUENTE_INGRESOS)),
  fuente_ingresos_otro: z.string().nullable().optional(),
  paises_ingresos: z.array(z.string()),
  paises_actividad_economica: z.array(z.string()),
  suma_aproximada_ingresos: z.string(),

  persona_autorizada_nombre: z.string(),
  persona_autorizada_email: z.string().email(),
  persona_autorizada_telefono: z.string(),
  persona_autorizada_direccion: z.string(),
  persona_autorizada_pais: z.string(),
  persona_autorizada_ciudad: z.string(),
  persona_autorizada_usar_correspondencia: z.boolean().nullable().optional(),

  domicilio_proporcionado: z.enum(DOMICILIO_PROPORCIONADO),
  domicilio_direccion: z.string(),
  domicilio_pais: z.string(),
  domicilio_ciudad: z.string(),
  domicilio_telefono: z.string(),

  correspondencia: z.boolean(),
  correspondencia_tipo: z.enum(CORRESPONDENCIA_TIPO).nullable().optional(),
  correspondencia_email: z.string().email().nullable().optional(),
  correspondencia_nombre: z.string().nullable().optional(),
  correspondencia_direccion: z.string().nullable().optional(),
  correspondencia_pais: z.string().nullable().optional(),
  correspondencia_ciudad: z.string().nullable().optional(),
  correspondencia_telefono: z.string().nullable().optional(),
  correspondencia_persona_contacto: z.string().nullable().optional(),

  facturacion: z.boolean(),
  facturacion_nombre: z.string(),
  facturacion_atencion: z.string(),
  facturacion_direccion: z.string(),
  facturacion_pais: z.string(),
  facturacion_ciudad: z.string(),
  facturacion_telefono: z.string(),
}).partial();
export type IncorporationRequest = z.infer<typeof IncorporationRequestSchema>;

export const IncorporationContactSchema = z.object({
  id: z.number().optional(),
  incorporation_request_id: z.number().optional(),
  contact_type: z.enum(CONTACT_TYPE),
  rol: z.array(z.enum(CONTACT_ROL)),
  nombre: z.string(),
  soy_quien_completa_registro: z.boolean(),
  email: z.string().email(),
  telefono: z.string(),
  direccion: z.string(),
  nacionalidades: z.array(z.string()),
  nacionalidad_principal: z.string().optional(),
  cargo: z.enum(CARGO_DIRECTOR),
  porcentaje_beneficio: z.number(),
  residencia_fiscal: z.string(),
  n_certificados: z.string().optional(),
  es_beneficiario: z.enum(SI_NO_OPTIONS).optional(),
});
export type IncorporationContact = z.infer<typeof IncorporationContactSchema>;

export type IncorporationWizardInitialData = {
  incorporationRequest: IncorporationRequest;
  incorporationContact: IncorporationContact[];
  currentUser?: { id: number; email: string };
  incorporationStripePrice?: StripePrice;
  path: string
};

// Stripe "Price" (simplificado)
export type StripePrice = {
  id: string;
  object: "price";
  currency: string;
  unit_amount: number | null;
  unit_amount_decimal: string | null;
};